/*
 *   File : report.js
 *   Author : https://evoqins.com
 *   Description : Report download page
 *   Integrations : NA
 *   Version : 1.0.0
*/

import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify';
import { format, subYears } from 'date-fns';
import html2pdf from 'html2pdf.js';

// Components
import { BreadCrumbs } from '../../Component/Other';
import { Footer } from '../../Component/Footer';
import { CustomDateRangePicker, CustomTextInput, DatePicker } from '../../Component/Form-Elements';
import { PrimaryButton, SecondaryButton } from '../../Component/Buttons';
import { APIService } from '../../Service';

// Assets
import Logo from "../../Assets/Images/index-fund-logo.svg";


// Custom Data
const BREADCRUMBS = [
    {
        label: "Home",
        path: "/"
    },
    {
        label: "Report",
        path: "/report"
    },
];

function Report() {

    const [capitalDateRange, setCapitalDateRange] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection'
        }
    ]);

    const [transactionDateRange, setTransactionDateRange] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection'
        }
    ]);

    const [selectedCapitalRange, setSelectedCapitalRange] = useState("");
    const [selectedTransactionRange, setSelectedTransactionRange] = useState("");

    const [capitalDateError, setCapitalDateError] = useState("");
    const [transactionDateError, setTransactionDateError] = useState("");
    const [showCapitalDatePicker, setShowCapitalDatePicker] = useState(false);
    const [showTransactionDatePicker, setShowTransactionDatePicker] = useState(false);

    const [capitalLoading, setCapitalLoading] = useState(false);
    const [capitalPDFLoading, setCapitalPDFLoading] = useState(false);
    const [transactionLoading, setTransactionLoading] = useState(false);
    const [transactionPDFLoading, setTransactionPDFLoading] = useState(false);
    const [holdingsLoading, setHoldingsLoading] = useState(false);
    const [holdingsPDFLoading, setHoldingsPDFLoading] = useState(false);

    const [holdingsDate, setHoldingsDate] = useState("");
    const [holdingsDateError, setHoldingsDateError] = useState("");
    const [showCalender, setShowCalender] = useState(null);


    // Closing modal when user clicks outside
    useEffect(() => {

        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });

        const _handleDatePicker = (e) => {
            if (e.target.closest("#capital-gain-report") === null) {
                setShowCapitalDatePicker(false);
            };

            if (e.target.closest("#transaction-report") === null) {
                setShowTransactionDatePicker(false);
            };
        };

        window.addEventListener("click", (e) => _handleDatePicker(e));
        return window.removeEventListener("click", (e) => _handleDatePicker(e));

    }, []);

    const _handleUpdateRange = (range, type) => {

        const _formatDate = (date) => {
            return date.toLocaleDateString("en-GB").replaceAll("/", "-");
        };

        if (type === 1) {
            setCapitalDateRange(range);
            setSelectedCapitalRange(_formatDate(range[0].startDate) + "  -  " + _formatDate(range[0].endDate));
            setShowCapitalDatePicker(false);
            setCapitalDateError("");
        } else if (type === 2) {
            setTransactionDateRange(range);
            setSelectedTransactionRange(_formatDate(range[0].startDate) + "  -  " + _formatDate(range[0].endDate));
            setShowTransactionDatePicker(false);
            setTransactionDateError("");
        }

    };

    const _handleDownloadCapitalGain = () => {
        if (selectedCapitalRange.length === 0) {
            setCapitalDateError("Select start and end date to proceed");
            return;
        }
        setCapitalLoading(true);
        _downLoadCapitalGainReport();
    };

    const _handleDownloadTransactionReport = () => {
        if (selectedTransactionRange.length === 0) {
            setTransactionDateError("Select start and end date to proceed");
            return;
        }
        setTransactionLoading(true);
        _downLoadTransactionReport();
    };

    const _handleDownloadHoldingsReport = () => {

        if (holdingsDate.length === 0) {
            setHoldingsDateError("Select holdings as on date");
            return;
        }
        setHoldingsLoading(true);
        _downLoadHoldingsReport();
    };

    const _handleDownloadPDFHoldingsReport = () => {

        if (holdingsDate.length === 0) {
            setHoldingsDateError("Select holdings as on date");
            return;
        }
        setHoldingsPDFLoading(true);
        _downLoadPDFHoldingsReport();
    };

    const _handleDownloadPDFCapitalGain = () => {
        if (selectedCapitalRange.length === 0) {
            setCapitalDateError("Select start and end date to proceed");
            return;
        }
        setCapitalPDFLoading(true);
        _downLoadPDFCapitalGainReport();
    };

    const _handleDownloadPDFTransactionReport = () => {
        if (selectedTransactionRange.length === 0) {
            setTransactionDateError("Select start and end date to proceed");
            return;
        }
        setTransactionPDFLoading(true);
        _downLoadPDFTransactionReport();
    };

    const _downloadHtmlAsPdf = (htmlResponse, setPdfLoading, filename = "report.pdf") => {
        const htmlString =
            typeof htmlResponse === "string"
                ? htmlResponse
                : htmlResponse?.data?.html && typeof htmlResponse.data.html === "string"
                    ? htmlResponse.data.html
                    : null;

        if (htmlString && htmlString.trim()) {
            const container = document.createElement("div");
            container.innerHTML = htmlString;
            document.body.appendChild(container);

            const img = container.querySelector(".header-logo");
            img.src = Logo;

            // Making page non scroallable due html injection in dom for PDF generation
            document.body.style.overflow = "hidden";


            html2pdf().set({
                margin: 8,
                filename,
                html2canvas: {
                    scale: 6, // ultra quality max safe value
                },
                jsPDF: { orientation: "landscape", format: "a4", unit: "mm" },
            }).from(container).save().then(() => {
                document.body.removeChild(container);
                setPdfLoading(false);
                // Removing overflow hidden
                document.body.style.overflow = "unset";
            }).catch(() => {
                document.body.removeChild(container);
                setPdfLoading(false);
                toast.dismiss();
                toast.error("Failed to generate PDF.", { className: "e-toast" });
                // Removing overflow hidden
                document.body.style.overflow = "unset";
            });
            return;
        }

        if (htmlResponse && typeof htmlResponse === "object" && htmlResponse.message) {
            toast.dismiss();
            toast.error(htmlResponse.message, { className: "e-toast" });
        } else {
            toast.dismiss();
            toast.error("Failed to load the report.", { className: "e-toast" });
        }
        setPdfLoading(false);
    };

    //handle change date
    const _handleHoldingsDateChange = (date) => {
        setHoldingsDate(date);
        setHoldingsDateError("");
    };

    const _handleCloseCalendar = (status) => {
        if (status) {
            setShowCalender(null);
        }
    };

    const _downLoadCapitalGainReport = () => {

        let is_post = true;
        let url = 'profile/capitalgain-report';

        let data = {
            "from_date": capitalDateRange[0].startDate.toLocaleDateString("en-Uk").split("/").reverse().join("-"),
            "till_date": capitalDateRange[0].endDate.toLocaleDateString('en-UK').split("/").reverse().join("-")
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {

                let a = document.createElement("a");
                a.href = response.data.url;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);

                toast.dismiss();
                toast.success("Capital gain report generated successfully", { className: "e-toast", });

            } else {
                toast.dismiss();
                toast.error(response.message, { className: "e-toast", });
            }

            setCapitalLoading(false);
        });
    };

    const _downLoadTransactionReport = () => {
        let is_post = true;
        let url = 'profile/transaction-report';

        let data = {
            "from_date": transactionDateRange[0].startDate.toLocaleDateString("en-Uk").split("/").reverse().join("-"),
            "till_date": transactionDateRange[0].endDate.toLocaleDateString("en-Uk").split("/").reverse().join("-"),
        }

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {

                let a = document.createElement("a");
                a.href = response.data.url;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);

                toast.dismiss();
                toast.success("Transaction report generated successfully", { className: "e-toast", });

            } else {
                toast.dismiss();
                toast.error(response.message, { className: "e-toast", });
            }

            setTransactionLoading(false);
        });
    };

    const _downLoadHoldingsReport = () => {

        let is_post = true;
        let url = 'profile/holdings-report';
        let data = {
            "as_on": format(holdingsDate, 'yyyy-MM-dd')
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {

                let a = document.createElement("a");
                a.href = response.data.url;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);

                toast.dismiss();
                toast.success("Holdings report generated successfully", { className: "e-toast", });

            } else {
                toast.dismiss();
                toast.error(response.message, { className: "e-toast", });
            }

            setHoldingsLoading(false);
        });
    };

    const _downLoadPDFCapitalGainReport = () => {
        let is_post = true;
        let url = "profile/capitalgain-html";
        let data = {
            from_date: capitalDateRange[0].startDate.toLocaleDateString("en-Uk").split("/").reverse().join("-"),
            till_date: capitalDateRange[0].endDate.toLocaleDateString("en-UK").split("/").reverse().join("-"),
        };

        APIService(is_post, url, data).then((response) => {
            _downloadHtmlAsPdf(response, setCapitalPDFLoading, "capital_gain_report.pdf");
        });
    };

    const _downLoadPDFTransactionReport = () => {
        let is_post = true;
        let url = "profile/transaction-html";
        let data = {
            from_date: transactionDateRange[0].startDate.toLocaleDateString("en-Uk").split("/").reverse().join("-"),
            till_date: transactionDateRange[0].endDate.toLocaleDateString("en-Uk").split("/").reverse().join("-"),
        };

        APIService(is_post, url, data).then((response) => {
            _downloadHtmlAsPdf(response, setTransactionPDFLoading, "transaction_report.pdf");
        });
    };

    const _downLoadPDFHoldingsReport = () => {

        let api_version = 2;
        let is_post = true;
        let url = 'profile/holdings-html';
        let data = {
            "as_on": format(holdingsDate, 'yyyy-MM-dd')
        };

        APIService(is_post, url, data, null, api_version).then((html_response) => {
            _downloadHtmlAsPdf(html_response, setHoldingsPDFLoading, "holdings_report.pdf");
        });
    };

    return (
        <>
            <div className="row flex-column min-h-100vh pb-4 pb-sm-5 e-bg-lotion px-4 px-sm-5 ps-lg-4 pe-lg-0 padding-26px-top e-border-radius-TL-BL-24px">
                <BreadCrumbs data={BREADCRUMBS} />
                <div className="col-lg-11 p-0 d-flex flex-column gap-4 mt-3 mt-sm-4 mb-5">
                    <div className='col col-sm-7 col-md-6 col-lg-5 col-xl-4'>
                        <div id="capital-gain-report"
                            className='position-relative mb-1'>
                            <CustomTextInput
                                label="Capital Gain Report"
                                placeholder="Start date - End date"
                                type="text"
                                maxLength={1000}
                                errorMsg={capitalDateError}
                                value={selectedCapitalRange}
                                readOnly={true}
                                className="cursor-pointer w-100 px-3 padding-12px-top padding-12px-bottom e-font-14 line-height-24px"
                                labelStyle="e-font-weight-500"
                                icon={"calendar"}
                                onClick={() => {
                                    setShowCapitalDatePicker((prev) => !prev);
                                    setShowTransactionDatePicker(false);
                                    setShowCalender(false);
                                    setCapitalDateError("");
                                }} />
                            {
                                showCapitalDatePicker &&
                                <CustomDateRangePicker
                                    initialRange={capitalDateRange}
                                    onUpdate={(range) => _handleUpdateRange(range, 1)}
                                    cancel={() => setShowCapitalDatePicker(false)}
                                    minDate={undefined}
                                    maxDate={new Date()}
                                    leftAlign={true} />
                            }

                        </div>
                        <div className="d-flex gap-3">
                            <PrimaryButton name="Download report"
                                disabled={capitalLoading || capitalPDFLoading}
                                loading={capitalLoading}
                                className="w-180px e-font-14 mt-2 py-2"
                                onPress={_handleDownloadCapitalGain} />
                            <SecondaryButton name="Download PDF"
                                type={2}
                                disabled={capitalLoading || capitalPDFLoading}
                                loading={capitalPDFLoading}
                                className="w-180px e-font-14 mt-2 py-2 e-border-bright-gray e-bg-lotion"
                                onPress={_handleDownloadPDFCapitalGain} />
                        </div>
                    </div>

                    <div className='col col-sm-7 col-md-6 col-lg-5 col-xl-4'>
                        <div id="transaction-report"
                            className='position-relative mb-1'>
                            <CustomTextInput
                                label="Transaction Report"
                                placeholder="Start date - End date"
                                type="text"
                                maxLength={1000}
                                value={selectedTransactionRange}
                                errorMsg={transactionDateError}
                                readOnly={true}
                                className="cursor-pointer w-100 px-3 padding-12px-top padding-12px-bottom e-font-14 line-height-24px"
                                labelStyle="e-font-weight-500"
                                icon={"calendar"}
                                onClick={() => {
                                    setShowTransactionDatePicker((prev) => !prev);
                                    setShowCapitalDatePicker(false);
                                    setShowCalender(false);
                                    setTransactionDateError("");
                                }}
                            />
                            {
                                showTransactionDatePicker &&
                                <CustomDateRangePicker
                                    initialRange={transactionDateRange}
                                    onUpdate={(range) => _handleUpdateRange(range, 2)}
                                    cancel={() => setShowTransactionDatePicker(false)}
                                    minDate={undefined}
                                    maxDate={new Date()}
                                    leftAlign={true} />
                            }
                        </div>
                        <div className="d-flex gap-3 ">
                            <PrimaryButton name="Download report"
                                disabled={transactionLoading || transactionPDFLoading}
                                loading={transactionLoading}
                                className="w-180px e-font-14 mt-2 py-2"
                                onPress={_handleDownloadTransactionReport} />
                            <SecondaryButton name="Download PDF"
                                type={2}
                                disabled={transactionLoading || transactionPDFLoading}
                                loading={transactionPDFLoading}
                                className="w-180px e-font-14 mt-2 py-2 e-border-bright-gray e-bg-lotion"
                                onPress={_handleDownloadPDFTransactionReport} />
                        </div>
                    </div>

                    <div className='col col-sm-7 col-md-6 col-lg-5 col-xl-4'>
                        <div id="holdings-report"
                            className='position-relative'>
                            <span className={`d-inline-block e-text-dark-charcoal e-font-weight-500 e-alt-font-poppins line-height-24px e-font-14 pb-2`}>
                                Holdings Report
                            </span>

                            <DatePicker value={holdingsDate}
                                minDate={subYears(new Date(), 100)}
                                maxDate={new Date()}
                                error={holdingsDateError}
                                wrapperClassName="padding-12px-tb"
                                showCalender={showCalender}
                                handleCloseCalendar={_handleCloseCalendar}
                                onChange={_handleHoldingsDateChange} />
                        </div>
                        <div className="d-flex gap-3">
                            <PrimaryButton name="Download report"
                                disabled={holdingsLoading ? true : false}
                                loading={holdingsLoading ? true : false}
                                className="w-180px e-font-14  py-2 mt-4  mt-sm-4"
                                onPress={_handleDownloadHoldingsReport} />
                            <SecondaryButton name="Download PDF"
                                type={2}
                                disabled={holdingsPDFLoading ? true : false}
                                loading={holdingsPDFLoading ? true : false}
                                className="w-180px e-font-14  py-2 mt-4  mt-sm-4 e-border-bright-gray e-bg-lotion"
                                onPress={_handleDownloadPDFHoldingsReport} />
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    )
}

export default Report;
